import React, { useCallback, useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkForm, { Field } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import EmojiAddIcon from '@atlaskit/icon/core/migration/emoji-add';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import type { ExternalError } from '@atlaskit/jql-editor';
import { useIntl } from '@atlassian/jira-intl';
import { getCollectionDetailsPageUrl } from '@atlassian/jira-polaris-component-collections/src/common/utils/collections.tsx';
import {
	getJQLForCollection,
	getFieldsFromJql,
} from '@atlassian/jira-polaris-component-collections/src/common/utils/jql.tsx';
import { useCollectionsActions } from '@atlassian/jira-polaris-component-collections/src/controllers/collections/index.tsx';
import { CollectionAdvancedFilters } from '@atlassian/jira-polaris-component-collection-advanced-filters/src/ui/index.tsx';
import {
	PROJECT_FIELDKEY,
	SUMMARY_FIELDKEY,
} from '@atlassian/jira-polaris-domain-field/src/field/constants.tsx';
import {
	VIEW_KIND_BOARD,
	VIEW_KIND_MATRIX,
	VIEW_KIND_TABLE,
	VIEW_KIND_TIMELINE,
} from '@atlassian/jira-polaris-domain-view/src/view/constants.tsx';
import { EmojiPicker } from '@atlassian/jira-polaris-lib-emoji-picker/src/ui/index.tsx';
import { ErrorContainer } from '@atlassian/jira-polaris-lib-inputs-error/src/ui/styled.tsx';
import { useNotifications } from '@atlassian/jira-polaris-lib-notifications/src/controllers/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import type { Collection } from '@atlassian/jira-polaris-domain-collection/src/index.tsx';
import { ProjectSelect } from '@atlassian/jira-polaris-project-select/src/ui/index.tsx';
import { IDEAS_COUNT_LIMIT } from '@atlassian/jira-polaris-component-collections/src/common/constants.tsx';
import { DRAWER_Z_INDEX } from '../../common/constants.tsx';
import { getFieldsKeysWithJql } from '../../services/fields/index.tsx';
import { FormFieldKeys } from './constants.tsx';
import { Footer } from './footer/index.tsx';
import messages from './messages.tsx';
import type { FormData } from './types.tsx';
import { useCollectionIdeasCount } from './utils/index.tsx';
import { FilterExplanation } from './filter-explanation/index.tsx';

const MAX_COLLECTION_NAME_LENGTH = 100;

export type FormProps = {
	onSuccess?: (collection: Collection) => void;
};

export const Form = ({ onSuccess }: FormProps) => {
	const [projectKeys, setProjectKeys] = useState<string[]>([]);
	const [filtersJql, setFiltersJql] = useState<string>('');
	const { formatMessage } = useIntl();
	const [selectedEmojiId, setSelectedEmojiId] = useState<string>();
	const { createCollection, createView } = useCollectionsActions();
	const { errorWithRefresh } = useNotifications();
	const { push } = useRouterActions();
	const ownerId = useAccountId() ?? '';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [ideasCount, isIdeasCountLoading, _ideasCountError, ideasCountRequest] =
		useCollectionIdeasCount(projectKeys, filtersJql);
	const [advancedFilterErrors, setAdvancedFilterErrors] = useState<ExternalError[]>([]);

	const validateNameField = useCallback(
		(value: string | undefined) => {
			if (value === undefined || value.trim().length === 0) {
				return formatMessage(messages.emptyNameError);
			}
			if (value.length > MAX_COLLECTION_NAME_LENGTH) {
				return formatMessage(messages.nameTooLongError);
			}

			return undefined;
		},
		[formatMessage],
	);

	const validateProjectsField = useCallback(
		(value: string[] | undefined) => {
			if (value === undefined || value.length === 0) {
				return formatMessage(messages.emptyProjectsError);
			}

			return undefined;
		},
		[formatMessage],
	);

	const handleSubmit = useCallback(
		async (formData: FormData) => {
			try {
				if (advancedFilterErrors.length) {
					return;
				}

				const jql = getJQLForCollection(projectKeys, filtersJql);

				let newIdeasCount = ideasCount;
				if (ideasCountRequest) {
					newIdeasCount = await ideasCountRequest;
				}
				if (newIdeasCount && newIdeasCount > IDEAS_COUNT_LIMIT) {
					return;
				}

				const collection = await createCollection({
					name: formData[FormFieldKeys.COLLECTION_NAME],
					jql,
					emoji: selectedEmojiId,
					ownerId,
					createAnalyticsEvent,
				});

				if (!collection) {
					return;
				}

				const fieldsInJQL = await getFieldsKeysWithJql(jql);

				const fields = [
					SUMMARY_FIELDKEY,
					PROJECT_FIELDKEY,
					...getFieldsFromJql(filtersJql, fieldsInJQL),
				];

				await createView(collection.uuid, {
					visualizationType: VIEW_KIND_TABLE,
					name: formatMessage(messages.listViewName),
					fields,
				});
				await createView(collection.uuid, {
					visualizationType: VIEW_KIND_TIMELINE,
					name: formatMessage(messages.timelineViewName),
					fields,
				});
				await createView(collection.uuid, {
					visualizationType: VIEW_KIND_BOARD,
					name: formatMessage(messages.boardViewName),
					fields,
				});
				await createView(collection.uuid, {
					visualizationType: VIEW_KIND_MATRIX,
					name: formatMessage(messages.matrixViewName),
					fields,
				});

				push(getCollectionDetailsPageUrl(collection.uuid));
				onSuccess?.({ ...collection });
			} catch (err) {
				errorWithRefresh({
					title: formatMessage(messages.errorTitle),
					description: formatMessage(messages.errorDescription),
				});
			}
		},
		[
			projectKeys,
			filtersJql,
			createCollection,
			selectedEmojiId,
			ownerId,
			createAnalyticsEvent,
			createView,
			formatMessage,
			push,
			onSuccess,
			ideasCount,
			errorWithRefresh,
			ideasCountRequest,
			advancedFilterErrors.length,
		],
	);

	return (
		<Box xcss={mainContainerStyles}>
			<AkForm<FormData> onSubmit={handleSubmit}>
				{({ formProps, submitting, setFieldValue, getValues }) => (
					<Box
						as="form"
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...formProps}
						name="collection-create-form"
						xcss={formStyles}
					>
						<Stack xcss={scrollableContainerStyles}>
							<Heading size="large">{formatMessage(messages.heading)}</Heading>
							<Box paddingBlockStart="space.050" paddingBlockEnd="space.200">
								<Text as="p">{formatMessage(messages.description)}</Text>
							</Box>
							<Field
								label={formatMessage(messages.roadmapNameFieldLabel)}
								name={FormFieldKeys.COLLECTION_NAME}
								isRequired
								validate={validateNameField}
							>
								{({ fieldProps, error, meta }) => {
									const isInvalid =
										fieldProps.isInvalid && !!error && (meta.submitFailed || meta.dirty);

									return (
										<>
											<Textfield
												{...fieldProps}
												isInvalid={isInvalid}
												isRequired={false}
												testId="polaris-component-collection-create.ui.form.collection-name"
												autoFocus
												placeholder={formatMessage(messages.roadmapNameFieldPlaceholder)}
												elemBeforeInput={
													<Box paddingInlineStart="space.025">
														<EmojiPicker
															onEmojiSelected={(emoji) => {
																setSelectedEmojiId(emoji?.id);
															}}
															readonly={submitting}
															selectedEmojiId={selectedEmojiId}
															emojiAddIcon={(iconProps) => (
																<EmojiAddIcon
																	{...iconProps}
																	color={token('color.icon')}
																	LEGACY_primaryColor={token('color.icon')}
																/>
															)}
															zIndex={DRAWER_Z_INDEX + 1}
														/>
													</Box>
												}
											/>
											{isInvalid && (
												<ErrorContainer data-testid="polaris-component-collection-create.ui.form.name-field-error">
													{error}
												</ErrorContainer>
											)}
										</>
									);
								}}
							</Field>
							<Field<string[]>
								label={formatMessage(messages.projectsSelectFieldLabel)}
								name={FormFieldKeys.PROJECTS}
								isRequired
								validate={validateProjectsField}
							>
								{({ fieldProps: { onChange, ...restFieldProps }, error, meta }) => {
									const isInvalid =
										restFieldProps.isInvalid && !!error && (meta.submitFailed || meta.dirty);
									const { id, isRequired, value, ...selectProps } = restFieldProps;

									return (
										<>
											<ProjectSelect
												{...selectProps}
												isInvalid={isInvalid}
												onChange={(newValue) => {
													const values = newValue.map((v) => v.value);
													if (Array.isArray(values)) {
														setProjectKeys(values);
													}
													onChange(values);
												}}
											/>

											{isInvalid && (
												<ErrorContainer data-testid="polaris-component-collection-create.ui.form.projects-field-error">
													{error}
												</ErrorContainer>
											)}
										</>
									);
								}}
							</Field>

							<Stack xcss={filtersContainerStyles} space="space.100">
								<Inline space="space.025" alignBlock="center">
									<Text color="color.text.subtle" weight="semibold" size="small">
										{formatMessage(messages.filtersLabel)}
									</Text>
									<FilterExplanation />
								</Inline>
								<CollectionAdvancedFilters
									value={getValues()[FormFieldKeys.FILTERS]}
									projectKeys={projectKeys}
									onChange={(value: string) => {
										setFiltersJql(value);
										setFieldValue(FormFieldKeys.FILTERS, value);
									}}
									errors={advancedFilterErrors}
									setErrors={setAdvancedFilterErrors}
								/>
							</Stack>
						</Stack>
						<Footer
							submitting={submitting}
							ideasCount={ideasCount}
							isIdeasCountLoading={isIdeasCountLoading}
						/>
					</Box>
				)}
			</AkForm>
		</Box>
	);
};

const mainContainerStyles = xcss({
	height: '100%',
});

const scrollableContainerStyles = xcss({
	paddingBlockStart: 'space.1000',
});

const formStyles = xcss({
	display: 'flex',
	gap: 'space.150',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	minHeight: '0',
	height: '100%',
	paddingInline: 'space.400',
	overflow: 'auto',
});

const filtersContainerStyles = xcss({ paddingBlockStart: 'space.300', minHeight: '0' });
